/**
 * paper-2-fill — 지면 파이프라인 ② 채움.
 *   틀(PaperTemplate) + 지도(TemplateMap) + 규칙(paper-1) + 데이터(QuoteItem[])
 *   → 채워진 지면(FilledPaper). 그리는 건 다음 단계(paper-3-html).
 *
 * ── 계보 (어휘 6호) ──  지면 ⊃ 구역 ⊃ 행 ⊃ 칸. 행 = 그리드 가로 한 줄, 층은 하나.
 *   행의 타입: 값행 · 품번 줄 · 대체 줄 · 우리 줄(덧붙임 계열) · 그대로 행 · 구성 머리 행 · SUMMARY 행.
 *   품목은 층이 아니라 묶음 — 값행 + 딸린 줄들(outPaper.items 가 자리 기록).
 *   행 타입을 가르는 것은 문구의 주인: 틀이 주인이면 copy, 우리가 주인이면 fill.
 *
 * ── 값의 두 경로 ──  칸에 값을 쓰는 손은 이 파일뿐이다(그리기는 받은 대로만 그림).
 *   구역 완결   : 구역을 만들 때 그 구역의 값까지 그 자리에서 앉힌다 — 값은 알려지는
 *                 시점에 쓴다 (합계 칸이 있는 구역은 합계를 알 수 있는 순서에 조립됨:
 *                 파트 푸터는 품목들 뒤, 서버 헤더는 계산이 데이터만으로 가능)
 *   편집        : 칸에 직접 쓰지 않는다 — QuoteItem 수정 → 통 재채움(refill)
 *
 * ── 조립 순서 ──  관문(지도 정합) → 계산(합계) → 구역 조립(위→아래, 각 구역 완결) → 지면으로.
 *
 * ── 왜 틀과 지도가 따로인가 — 엑셀 한 장, 원고 둘 ──
 *   틀   = 기계(추출기)가 뽑은 생김새. 서식이 바뀌면 통째 재생성(자동).
 *   지도 = 사람이 실측해 적은 뜻(구역·서식 원본 줄·값 칸 자리). 코드 리뷰로 바뀜.
 *   연결 열쇠는 이름이 아니라 엑셀 좌표 — 줄 번호는 지도가, 열 번호는 열 명세(xlsxCol)가 준다.
 *
 * ── 이 파일의 동사 사전 (어휘 5·6호) ──
 *   copy~  : 그대로 — 틀 문구가 곧 문서라 유지 (값 칸 문구만 앉힘)
 *   fill~  : 새로 채움 — 서식만 차용, 빈 종이 시작(옛 견적 잔상 차단)
 *   to~    : 모양 바꾸기 (toQuoteItem 가족 · toRegion = 행 초안 앉히기)
 *   ~Of    : 대상에서 계산해 낸 값 (spanOf · totalsOfItems/totalsOfConfigs · regionsOf)
 *   make~  : 틀에 없는 칸 객체를 짓는다 — 리터럴 하나가 결과 (makeOurLineCells · makeMarginAmountCell · makeMarginColumnHead).
 *            create 는 API 층(서버 레코드 생성)이 점유한 말이라 여기선 안 쓴다.
 *
 * 파일 배치는 하향식: ①지도 → ②명사 → ③조립부 → ④구역 → ⑤품목 묶음 → ⑥행 재료 → ⑦값 → ⑧어댑터.
 */
import { specCategoryOrder, toSpecCategory } from '@hcltech/shared/server-config/spec-category';
import { assert } from '../../lib/assert';
import { colLetterToNumber, colNumberToLetter, parseAddress } from '../../lib/excel-address';
import type { PaperTemplate, TemplateCell, FilledCell, InPaper, EditStamp } from './paper-0-cells';
import type { ColumnSpec, QuoteItem, QuoteCtx, QuoteTitle } from './paper-1-specs';
import {
  columnSpecs, sellUnitKrw, sellAmountKrw, costUnitKrw, costAmountKrw, marginKrw, marginPercent,
  sourceLabel, partNumberLineText, substitutionLineText, conditionText, getRentalTotalKrw, NO_TITLE,
} from './paper-1-specs';
import { buildServerDescription } from '@hcltech/shared/server-config/shorthand';

/* ═══════════════ ① 지도 — 지면 전체를 구역으로 빠짐없이 선언한다 ═══════════════ */

/** 덧붙임 줄(품번·대체)의 서식 원본 — 어느 줄의 옷을 입고, 문구가 어느 열에서 시작하나. */
export interface LineStyleSource { styleRow: number; textStartCol: string }

/**
 * 파트 견적 지도 — 줄 하나도 소속 없이 남지 않게 세 구역으로 가른다.
 * 사람이 엑셀을 실측해 적는다. 정합은 assertMapCoversTemplate 이 지킨다.
 */
export interface TemplateMap {
  /** 헤더 구역 — 그대로 (늘어나지도 밀리지도 않음). */
  header: {
    kind: 'fixed';
    fromRow: number; toRow: number;
    /** 품목표 머리 행('구분·수량·단가·금액' 라벨 줄) — 헤더 구역 안에 산다. 우리 뷰는 이 줄에 판매 라벨과 마진 열 머리를 얹는다. */
    itemTableHeadRow: number;
    /** 지면 수준 값 칸(표제) — 실측 그대로 전부 필수. 파트는 기간이 한 칸, 담당 줄이 두 자리. */
    valueCells: {
      total: string; hangul: string;                            // 합계 밴드(헤더에 앉는다: 총액 · 일금)
      dealName: string; quoteNo: string; customerName: string;   // 고객사(귀중) · 건명 · 견적번호
      issuedAt: string; validUntil: string;                     // 발행일 · 유효기한 (두 칸)
      contactName: string; contactPhone: string; contactEmail: string;  // 수신(고객 담당) 세 줄
      assigneeName: string; assigneeEmail: string;              // 담당(우리) 이름 · 메일
    };
  };
  /** 품목표 구역 — 품목 수만큼 늘어난다. */
  itemTable: {
    kind: 'unfixed';
    fromRow: number; toRow: number;
    /** 값행이 '서식만' 입어올 원본 행. */
    itemStyleRow: number;
    partNumberLine: LineStyleSource;    // 품번 줄(고객에게 실림)
    substitutionLine: LineStyleSource;  // 대체 줄(우리 줄 — 안 실림)
  };
  /** 푸터 구역 — 그대로, 품목표가 늘어난 만큼 밀리기만 함. */
  footer: {
    kind: 'fixed';
    fromRow: number; toRow: number;
    /** 지면 수준 값 칸들의 주소 — 실측 그대로 전부 필수. 틀에 없는 칸은 필드 자체가 없다(지도 오기는 컴파일 즉사). */
    valueCells: { itemsTotalLabel: string; itemsTotal: string; vat: string; total: string };  // SUMMARY: 품목 합계(라벨+값) · 부가세 · 합계
    subtitleText: (itemCount: number) => string;   // 품목 합계 라벨 문구(개별 파트 N종)
  };
}

/**
 * 서버 견적 지도 — 네 구역. 구성 견본 구역은 원본 엑셀의 견본 1벌로,
 * 여기서 서식·문구를 골라 구성 수만큼 재생성한다(견본 자체는 지면에 안 실림).
 */
export interface ServerTemplateMap {
  header: {
    kind: 'fixed';
    fromRow: number; toRow: number;
    /** 지면 수준 값 칸 — 표제 + 합계(서버는 합계가 헤더에 앉는다). 실측 그대로 전부 필수. */
    valueCells: {
      total: string; hangul: string; subtitle: string;   // 합계 셋(서버는 합계가 헤더에 앉는다)
      dealName: string; quoteNo: string; customerName: string;   // 고객사(귀중) · 건명 · 견적번호
      issuedAt: string; validUntil: string;                     // 발행일 · 유효기한 (두 칸)
      contactName: string; contactPhone: string; contactEmail: string;  // 수신(고객 담당) 세 줄
      assigneeName: string; assigneeEmail: string;              // 담당(우리) 이름 · 메일
    };
    subtitleText: (configCount: number, unitCount: number) => string;
  };
  configSample: {
    kind: 'unfixed';                    // 구성 수만큼 늘어난다 (견본 자체는 안 실림)
    fromRow: number; toRow: number;
    /** 구성 머리 행들(이름·칩·소계) — 구성마다 이 구간을 다시 베낀다. */
    head: {
      fromRow: number; toRow: number;
      /** 품목표 머리 행 — 구성 머리 안에 산다(구성마다 반복). 우리 뷰는 판매 라벨과 마진 열 머리를 얹는다. */
      itemTableHeadRow: number;
      valueCells: { index: string; name: string; subtotal: string; specLine: string; qtyLine: string };
      chips: ChipSlot[];               // 칩 6칸의 주소 (부품 매핑 순서)
    };
    itemStyleRow: number;
    partNumberLine: LineStyleSource;
    substitutionLine: LineStyleSource;
  };
  summary: {
    kind: 'summary';                     // 하나뿐인 구역 — 좌측(labelCol 앞)은 고정 · 우측은 재생성
    fromRow: number; toRow: number;
    headerRow: number;
    termsRows: number[];               // 좌측 TERMS 값 줄들(그대로 유지)
    subtotalRow: number; netRow: number; vatRow: number; totalRow: number; noteRow: number;
    labelCol: string; valueCol: string; // 우측 SUMMARY 라벨/값 열 — 이 열부터 오른쪽이 재생성부
  };
  /**
   * 개별 파트 구역 — 서버 구성 외에 추가로 실린 파트들(구형 문서의 "Add Option Parts"). 구성들 뒤, SUMMARY 앞.
   * 틀에 자리가 없어 옷만 빌린다: 머리 띠 = 구성 머리 첫 줄(번호 칸 비움 · 이름 칸 문구 · 소계 칸 = 개별 파트 합),
   * 라벨 행 = 품목표 머리 행, 품목 행들 = configSample 의 서식 원본. 개별 파트가 0개면 구역 자체가 없다.
   */
  extraParts: { kind: 'unfixed'; headStyleRow: number; itemTableHeadRow: number };
  footer: { kind: 'fixed'; fromRow: number; toRow: number };
}

/** 스펙 칩 한 칸(라벨/값/상세 주소). */
export interface ChipSlot { label: string; value: string; detail?: string }

/** 지도가 틀을 빈틈·겹침 없이 덮는지 — 사람 실측의 정합 검사. 어긋나면 채움 전에 즉사. */
function assertMapCoversTemplate(map: TemplateMap, template: PaperTemplate): void {
  assert(map.header.fromRow === 1, '지도: 헤더 구역은 1행부터여야 한다');
  assert(map.header.toRow + 1 === map.itemTable.fromRow, '지도: 헤더와 품목표 사이에 소속 없는 줄');
  assert(map.itemTable.toRow + 1 === map.footer.fromRow, '지도: 품목표와 푸터 사이에 소속 없는 줄');
  assert(map.footer.toRow === template.rows.length, '지도: 푸터가 틀 끝까지 덮지 않음');
  assert(map.header.fromRow <= map.header.itemTableHeadRow && map.header.itemTableHeadRow <= map.header.toRow,
    '지도: 품목표 머리 행이 헤더 구역 밖');
  const styleRows = [
    ['값줄', map.itemTable.itemStyleRow],
    ['품번 줄', map.itemTable.partNumberLine.styleRow],
    ['대체 줄', map.itemTable.substitutionLine.styleRow],
  ] as const;
  for (const [name, styleRow] of styleRows) {
    assert(map.itemTable.fromRow <= styleRow && styleRow <= map.itemTable.toRow,
      `지도: ${name} 서식 원본이 품목표 구역 밖`);
  }
}

function assertServerMapCoversTemplate(map: ServerTemplateMap, template: PaperTemplate): void {
  assert(map.header.fromRow === 1, '서버 지도: 헤더 구역은 1행부터여야 한다');
  assert(map.header.toRow + 1 === map.configSample.fromRow, '서버 지도: 헤더와 구성 견본 사이에 소속 없는 줄');
  assert(map.configSample.toRow + 1 === map.summary.fromRow, '서버 지도: 구성 견본과 SUMMARY 사이에 소속 없는 줄');
  assert(map.summary.toRow + 1 === map.footer.fromRow, '서버 지도: SUMMARY 와 푸터 사이에 소속 없는 줄');
  assert(map.footer.toRow === template.rows.length, '서버 지도: 푸터가 틀 끝까지 덮지 않음');
  const sample = map.configSample;
  const inSample = (name: string, templateRowNumber: number) =>
    assert(sample.fromRow <= templateRowNumber && templateRowNumber <= sample.toRow, `서버 지도: ${name}이 구성 견본 구역 밖`);
  inSample('구성 머리 시작', sample.head.fromRow);
  inSample('구성 머리 끝', sample.head.toRow);
  assert(sample.head.fromRow <= sample.head.itemTableHeadRow && sample.head.itemTableHeadRow <= sample.head.toRow,
    '서버 지도: 품목표 머리 행이 구성 머리 밖');
  inSample('값줄 서식 원본', sample.itemStyleRow);
  inSample('품번 줄 서식 원본', sample.partNumberLine.styleRow);
  inSample('대체 줄 서식 원본', sample.substitutionLine.styleRow);
  inSample('개별 파트 머리 띠 서식 원본', map.extraParts.headStyleRow);
  inSample('개별 파트 라벨 행', map.extraParts.itemTableHeadRow);
}

/** 파트 v3 '견적서' 지도 (사람 실측 2026-08-27 — 틀이 바뀌면 사람이 갱신). */
export const PART_MAP: TemplateMap = {
  header: {
    kind: 'fixed', fromRow: 1, toRow: 20, itemTableHeadRow: 20,
    valueCells: {
      total: 'AB17', hangul: 'C18',                       // 합계 밴드(헤더 r17~18)
      dealName: 'C7', quoteNo: 'AH6', customerName: 'C6',
      issuedAt: 'AH7', validUntil: 'AH8',
      contactName: 'F10', contactPhone: 'F11', contactEmail: 'F12',
      assigneeName: 'AH10', assigneeEmail: 'AH12',
    },
  },
  itemTable: {
    kind: 'unfixed', fromRow: 21, toRow: 36, itemStyleRow: 21,
    partNumberLine: { styleRow: 22, textStartCol: 'I' },
    substitutionLine: { styleRow: 23, textStartCol: 'I' },
  },
  footer: {
    kind: 'fixed', fromRow: 37, toRow: 48,
    valueCells: { itemsTotalLabel: 'X39', itemsTotal: 'AI39', vat: 'AI40', total: 'AI41' },  // SUMMARY r39~41
    subtitleText: (itemCount) => `품목 합계  (개별 파트 ${itemCount}종)`,
  },
};

/** 파트 임대 v3 '파트임대' 지도 (사람 실측 2026-09-03). 파트 판매와 같은 뼈대·다른 품목표 열(임대 5열).
 *  값 칸(표제·밴드)은 파트 판매와 같은 자리 — 헤더 서식이 같다. 다른 건 SUMMARY 라벨(임대 총액)뿐. */
export const PART_RENTAL_MAP: TemplateMap = {
  header: {
    kind: 'fixed', fromRow: 1, toRow: 20, itemTableHeadRow: 20,
    valueCells: {
      total: 'AB17', hangul: 'C18',                       // 합계 밴드(헤더 r17~18)
      dealName: 'C7', quoteNo: 'AH6', customerName: 'C6',
      issuedAt: 'AH7', validUntil: 'AH8',
      contactName: 'F10', contactPhone: 'F11', contactEmail: 'F12',
      assigneeName: 'AH10', assigneeEmail: 'AH12',
    },
  },
  itemTable: {
    kind: 'unfixed', fromRow: 21, toRow: 28, itemStyleRow: 21,
    partNumberLine: { styleRow: 22, textStartCol: 'I' },
    substitutionLine: { styleRow: 23, textStartCol: 'I' },   // P1 렌더는 대체 줄을 안 만든다(우리 줄 없음) — 자리만 실측
  },
  footer: {
    kind: 'fixed', fromRow: 29, toRow: 40,
    valueCells: { itemsTotalLabel: 'X30', itemsTotal: 'AI30', vat: 'AI31', total: 'AI32' },  // SUMMARY r30~32
    subtitleText: (itemCount) => `임대 총액  (파트 ${itemCount}종)`,
  },
};

/** 서버 v3 '견적서' 지도 (사람 실측). 구성 견본 = 원본 엑셀 19~69행의 견본 1벌. */
export const SERVER_MAP: ServerTemplateMap = {
  header: {
    kind: 'fixed', fromRow: 1, toRow: 18,
    valueCells: {
      total: 'AB17', hangul: 'C18', subtitle: 'AB18',
      dealName: 'C7', quoteNo: 'AH6', customerName: 'C6',
      issuedAt: 'AH7', validUntil: 'AH8',
      contactName: 'F10', contactPhone: 'F11', contactEmail: 'F12',
      assigneeName: 'AH10', assigneeEmail: 'AH11',
    },
    subtitleText: (configCount, unitCount) => `서버 ${configCount}식 (총 ${unitCount}대)  ·  통화 KRW`,
  },
  configSample: {
    kind: 'unfixed', fromRow: 19, toRow: 69,
    head: {
      fromRow: 20, toRow: 30, itemTableHeadRow: 30,
      valueCells: { index: 'D20', name: 'I20', subtotal: 'AG20', specLine: 'I21', qtyLine: 'AE21' },
      chips: [
        { label: 'D24', value: 'D25', detail: 'D26' },   // CPU
        { label: 'P24', value: 'P25', detail: 'P26' },   // MEMORY
        { label: 'AA24', value: 'AA25', detail: 'AA26' },// STORAGE
        { label: 'D28', value: 'G28' },                  // RAID
        { label: 'P28', value: 'S28' },                  // NIC
        { label: 'AA28', value: 'AD28' },                // PSU
      ],
    },
    itemStyleRow: 31,
    partNumberLine: { styleRow: 32, textStartCol: 'I' },
    substitutionLine: { styleRow: 35, textStartCol: 'I' },
  },
  summary: {
    kind: 'summary', fromRow: 70, toRow: 76,
    headerRow: 70, termsRows: [71, 72, 73, 74, 75],
    subtotalRow: 71, netRow: 73, vatRow: 74, totalRow: 75, noteRow: 76,
    labelCol: 'Y', valueCol: 'AI',
  },
  extraParts: { kind: 'unfixed', headStyleRow: 20, itemTableHeadRow: 30 },
  footer: { kind: 'fixed', fromRow: 77, toRow: 83 },
};

/**
 * 서버 임대 지도 — 하이브리드: 본문은 서버(구성 밴드), SUMMARY 는 파트식(3줄 고정 푸터).
 * 그래서 ServerTemplateMap 의 summary 구역(구성별 소계·Net 재생성) 대신 TemplateMap 의 footer(임대 총액·부가세·합계)를 쓴다.
 * 밴드머리·소계는 임대 문구로 갈린다(copyConfigHead 가 config.rentalTermMonths 로 판별).
 */
export interface ServerRentalTemplateMap {
  header: ServerTemplateMap['header'];
  configSample: ServerTemplateMap['configSample'];
  extraParts: ServerTemplateMap['extraParts'];
  footer: TemplateMap['footer'];               // 파트식 SUMMARY 푸터(임대 총액·부가세·합계)
}

/** 서버 임대 밴드머리 문구 — 'N개월  ×  N대  ×  월 ₩X'. 판매의 '수량 N대 × 1대 ₩X' 자리(밴드 AC21). */
function rentalBandSubtitle(months: number, serverQty: number, unitKrw: number): string {
  return `${months}개월   ×   ${serverQty}대   ×   월 ₩${formatWon(unitKrw)}`;
}

/** 서버 v3 '서버임대' 지도 (사람 실측 2026-09-03). 본문 구성 밴드는 서버 판매와 자리가 다르다(임대 열·칩 주소 재측). */
export const SERVER_RENTAL_MAP: ServerRentalTemplateMap = {
  header: {
    kind: 'fixed', fromRow: 1, toRow: 18,
    valueCells: {
      total: 'AB17', hangul: 'C18', subtitle: 'AB18',
      dealName: 'C7', quoteNo: 'AH6', customerName: 'C6',
      issuedAt: 'AH7', validUntil: 'AH8',
      contactName: 'F10', contactPhone: 'F11', contactEmail: 'F12',
      assigneeName: 'AH10', assigneeEmail: 'AH12',
    },
    subtitleText: () => 'VAT 포함  ·  통화 KRW',   // 서버 임대 밴드 부제는 파트식(정적) — '서버 N식'은 SUMMARY 라벨이 진다
  },
  configSample: {
    kind: 'unfixed', fromRow: 19, toRow: 71,
    head: {
      fromRow: 20, toRow: 31, itemTableHeadRow: 31,
      valueCells: { index: 'C20', name: 'F20', subtotal: 'AC20', specLine: 'F21', qtyLine: 'AC21' },
      chips: [
        { label: 'C24', value: 'C25', detail: 'C26' },    // CPU
        { label: 'S24', value: 'S25', detail: 'S26' },    // MEMORY
        { label: 'AE24', value: 'AE25', detail: 'AE26' }, // STORAGE
        { label: 'C29', value: 'G29' },                   // RAID
        { label: 'S29', value: 'W29' },                   // NIC
        { label: 'AE29', value: 'AI29' },                 // PSU
      ],
    },
    itemStyleRow: 32,
    partNumberLine: { styleRow: 33, textStartCol: 'I' },
    substitutionLine: { styleRow: 33, textStartCol: 'I' },   // 임대 약식은 대체 줄을 안 만든다 — 자리만 실측
  },
  extraParts: { kind: 'unfixed', headStyleRow: 20, itemTableHeadRow: 31 },
  footer: {
    kind: 'fixed', fromRow: 72, toRow: 83,
    valueCells: { itemsTotalLabel: 'X73', itemsTotal: 'AI73', vat: 'AI74', total: 'AI75' },  // SUMMARY r73~75
    subtitleText: (configCount) => `임대 총액  (서버 ${configCount}식 + 옵션)`,
  },
};

/** 서버 임대 지도가 틀을 빈틈·겹침 없이 덮는지 — 채움 전 즉사 검사(하이브리드: 서버 본문 + 파트식 푸터). */
function assertServerRentalMapCoversTemplate(map: ServerRentalTemplateMap, template: PaperTemplate): void {
  assert(map.header.fromRow === 1, '서버 임대 지도: 헤더 구역은 1행부터여야 한다');
  assert(map.header.toRow + 1 === map.configSample.fromRow, '서버 임대 지도: 헤더와 구성 견본 사이에 소속 없는 줄');
  assert(map.configSample.toRow + 1 === map.footer.fromRow, '서버 임대 지도: 구성 견본과 푸터 사이에 소속 없는 줄');
  assert(map.footer.toRow === template.rows.length, '서버 임대 지도: 푸터가 틀 끝까지 덮지 않음');
  const sample = map.configSample;
  const inSample = (name: string, rowNumber: number) =>
    assert(sample.fromRow <= rowNumber && rowNumber <= sample.toRow, `서버 임대 지도: ${name}이 구성 견본 구역 밖`);
  inSample('구성 머리 시작', sample.head.fromRow);
  inSample('구성 머리 끝', sample.head.toRow);
  assert(sample.head.fromRow <= sample.head.itemTableHeadRow && sample.head.itemTableHeadRow <= sample.head.toRow,
    '서버 임대 지도: 품목표 머리 행이 구성 머리 밖');
  inSample('값줄 서식 원본', sample.itemStyleRow);
  inSample('품번 줄 서식 원본', sample.partNumberLine.styleRow);
  inSample('개별 파트 라벨 행', map.extraParts.itemTableHeadRow);
}

/* ═══════════════ ② 채움 산출물 명사 ═══════════════ */

/** 지면 밖 겹이 품목 하나에 대해 아는 것 — 어느 행부터 몇 줄인가(거터가 여백을 품목 행에 맞추는 재료). 지면엔 안 실린다. */
export interface OutPaperItem {
  itemIndex: number;      // items[] 인덱스 (편집 data-idx 와 동일)
  id: number | null;      // quote_item id
  gridRowStart: number;   // 품목 첫 행이 앉은 그리드 행 번호 (CSS grid-row-start 그대로)
  span: number;           // 차지한 행 수 (2~4) — grid-row: start/span 과 1:1
}

/** 지면 밖 세 겹(거터·레일·하단바)이 보는 것 — 품목마다 한 줄 + 합계. 고객에게 절대 안 실림. */
export interface OutPaper { items: OutPaperItem[]; totals: Totals }

/** 합계 셋 — 공급가액 · 부가세 10% · 총액. */
export interface Totals { net: number; vat: number; total: number }

/**
 * 마진 금액 열 — 틀 밖 파생 열(우리 뷰 전용). 지도에 없는 이유: 고객 종이에 없는 열이라 사람이 실측할 게 없다.
 * 칸 좌표는 틀의 마지막 열 바로 다음(col) — 틀 칸 좌표를 하나도 밀지 않기 위해.
 * 화면에서는 금액 열(afterCol) 바로 뒤에 붙어 보인다 — 사이의 종이 여백 열을 접는다(toInPaper, 너비 0).
 * 값행엔 마진 금액, 우리 줄엔 마진율, 품목표 머리엔 '마진 금액'이 앉는다.
 */
interface MarginColumn { col: number; widthPx: number; afterCol: number }

/**
 * 채움 결과 — in/out 을 봉지에서부터 가른다:
 *   inPaper  = 지면 그 자체. 고객에게 실리고 xlsx 가 되는 것. 그리기(paper-3)가 소비.
 *   outPaper = 지면 밖 세 겹(거터·레일·하단바)이 보는 것. 고객에게 절대 안 실림.
 *              (totals 는 지면에도 찍힌 값의 숫자 사본 — 하단바가 지면을 파싱하지 않도록)
 */
export interface FilledPaper {
  inPaper: InPaper;
  outPaper: OutPaper;
}

/** 구역(또는 행 묶음) 하나 — 칸들 + 행 높이들. 행 수 = rowHeights.length.
 *  내부 살림 명사 — Filled~ 분사는 밖으로 나가는 계약(Cell·Template·Paper)에만 붙는다. */
interface Region { cells: FilledCell[]; rowHeights: number[] }

/** 품목 하나를 채운 결과 — 지면 전체(FilledPaper)와 같은 꼴: 지면 안 구역 + 지면 밖 한 줄. 채우는 순간 둘을 같이 만든다(나중에 역산하지 않는다). */
interface FilledItem { inPaper: Region; outPaper: OutPaperItem }

/** 행 초안 — 아직 자리에 앉지 않은 행: 칸들 + 행 높이.
 *  칸의 r 는 원산지 틀 행 번호 그대로(지면 자리 아님) — toRegion 이 지면 행으로 바꿔 앉힌다. */
interface RowDraft { cells: FilledCell[]; rowHeight: number }

/* ═══════════════ ③ 조립부 — 관문 → 계산 → 구역마다 한 문장(구역 완결) → 지면으로 ═══════════════ */

/** 파트 견적 채움. 열 명세는 파트 판(columnSpecs('part'))을 쓴다 — 유형은 함수로 가른다. */
export function fillPartPaper(
  template: PaperTemplate,
  map: TemplateMap,
  quoteItems: QuoteItem[],
  ctx: QuoteCtx,
  isCustomerView = false,                        // 고객 뷰: 우리 줄·대체 줄을 아예 안 만든다
  quoteTitle: QuoteTitle = NO_TITLE,             // 표제 — 없으면 헤더 값 칸이 틀 문구 그대로
): FilledPaper {
  assertMapCoversTemplate(map, template);                                        // 관문: 지도 정합
  const totals = totalsOfItems(quoteItems, ctx);                                 // 계산: 품목들의 합계 (데이터만으로)
  const specs = columnSpecs('part');
  const marginColumn = isCustomerView ? null : marginColumnOf(template, specs);  // 마진 열: 우리 뷰에만 (고객 뷰는 열 자체가 없다)
  const env: FillEnv = { template, specs, ctx, isCustomerView, isBrief: false, marginColumn, itemRowSources: map.itemTable };

  // 조립 — 구역마다 한 문장, 위에서 아래로. 값은 그 자리에서 앉힌다(구역 완결).
  //   합계 밴드는 헤더에 앉는다(v3.3): 총액·일금. SUMMARY(품목합계·부가세·합계)는 푸터.
  const headerRowTexts = new Map<string, string | null>([
    ...titleTexts(map.header.valueCells, quoteTitle),                 // 표제 칸들
    [map.header.valueCells.total, formatWon(totals.total)],          // 밴드: 총액
    [map.header.valueCells.hangul, hangulAmountKrw(totals.total)],   // 밴드: 일금
    ...sellLabelTexts(env, map.header.itemTableHeadRow),              // 우리 뷰: 품목표 머리 라벨 '판매단가'·'판매금액 합계'
  ]);
  let header = copyRegion(template, map.header, map.header.fromRow, headerRowTexts);
  header = appendMarginColumnHead(header, env, map.header, map.header.fromRow);   // 우리 뷰: 머리 행 끝에 '마진 금액'

  const itemsStart = map.header.toRow + 1;
  const items = fillItems(env, quoteItems, itemsStart);
  const itemRegions = items.map((filledItem) => filledItem.inPaper);
  const footer = copyRegion(template, map.footer, itemsStart + spanOf(itemRegions),
    footerTexts(map.footer, totals, quoteItems.length));

  return {
    inPaper: toInPaper(template, [header, ...itemRegions, footer], marginColumn), // 지면 순서: 헤더 → 품목들 → 푸터
    outPaper: { items: items.map((filledItem) => filledItem.outPaper), totals },
  };
}

/**
 * 파트 임대 견적 채움. 판매와 뼈대는 같고 열 명세(part-rental)·합계(임대 총액)가 다르다.
 * P1(렌더): 임대 열은 파생(읽기 전용) · 마진 열·우리 줄 없음(임대 마진·소스는 후속 P2).
 */
export function fillPartRentalPaper(
  template: PaperTemplate,
  map: TemplateMap,
  quoteItems: QuoteItem[],
  ctx: QuoteCtx,
  isCustomerView = false,
  quoteTitle: QuoteTitle = NO_TITLE,
): FilledPaper {
  assertMapCoversTemplate(map, template);                                        // 관문: 지도 정합
  const totals = totalsOfRentalItems(quoteItems);                                // 계산: 임대 금액합계의 합
  const specs = columnSpecs('part-rental');
  const env: FillEnv = { template, specs, ctx, isCustomerView, isBrief: false, marginColumn: null, itemRowSources: map.itemTable };

  const headerRowTexts = new Map<string, string | null>([
    ...titleTexts(map.header.valueCells, quoteTitle),
    [map.header.valueCells.total, formatWon(totals.total)],
    [map.header.valueCells.hangul, hangulAmountKrw(totals.total)],
  ]);
  const header = copyRegion(template, map.header, map.header.fromRow, headerRowTexts);

  const itemsStart = map.header.toRow + 1;
  const items = fillRentalItems(env, quoteItems, itemsStart);
  const itemRegions = items.map((filledItem) => filledItem.inPaper);
  const footer = copyRegion(template, map.footer, itemsStart + spanOf(itemRegions),
    footerTexts(map.footer, totals, quoteItems.length));

  return {
    inPaper: toInPaper(template, [header, ...itemRegions, footer], null),        // 임대 P1: 마진 열 없음
    outPaper: { items: items.map((filledItem) => filledItem.outPaper), totals },
  };
}

/** 임대 품목표 — 품목 하나당 [값행 + 품번 줄]. 판매의 우리 줄·대체 줄은 임대에 없다(P1). */
function fillRentalItems(env: FillEnv, quoteItems: QuoteItem[], gridRowStart: number): FilledItem[] {
  const filledItems: FilledItem[] = [];
  let nextGridRowStart = gridRowStart;
  for (const [itemIndex, item] of quoteItems.entries()) {
    const filledItem = fillRentalItemRows(env, item, itemIndex, nextGridRowStart);
    filledItems.push(filledItem);
    nextGridRowStart += filledItem.inPaper.rowHeights.length;
  }
  return filledItems;
}

/** 임대 품목 한 묶음 — 값행(임대 5열) + 품번 줄. 마진 열은 env.marginColumn=null 이라 값행에 안 붙는다. */
function fillRentalItemRows(env: FillEnv, item: QuoteItem, itemIndex: number, gridRowStart: number): FilledItem {
  const rows = [
    fillValueRow(env, item, itemIndex),
    fillLineRow(env, env.itemRowSources.partNumberLine, partNumberLineText(item)),
  ].filter((row): row is RowDraft => row != null);
  const inPaper = toRegion(rows, gridRowStart);
  return { inPaper, outPaper: { itemIndex, id: item.id, gridRowStart, span: inPaper.rowHeights.length } };
}

/** 품목표 — 품목 하나당 채운 품목 하나(quoteItems 와 1:1), 위에서 아래로. 편집 번호 = quoteItems 인덱스. */
function fillItems(env: FillEnv, quoteItems: QuoteItem[], gridRowStart: number): FilledItem[] {
  const filledItems: FilledItem[] = [];
  let nextGridRowStart = gridRowStart;
  for (const [itemIndex, item] of quoteItems.entries()) {
    const filledItem = fillItemRows(env, item, itemIndex, nextGridRowStart);
    filledItems.push(filledItem);
    nextGridRowStart += filledItem.inPaper.rowHeights.length;
  }
  return filledItems;
}

/** 구역들이 차지한 행 수 합 — 다음 구역의 시작 행 계산용. */
function spanOf(regions: Region[]): number {
  return regions.reduce((sum, region) => sum + region.rowHeights.length, 0);
}

/** 틀의 마진 금액 열 — 좌표는 마지막 열 다음 한 칸(엑셀 번호라 length+1), 너비 116px(금액 열과 같은 급), 금액 열 끝에 붙어 보인다. */
function marginColumnOf(template: PaperTemplate, specs: readonly ColumnSpec[]): MarginColumn {
  const amountSpec = specs.find((spec) => spec.id === 'amount');
  assert(amountSpec, '열 명세에 금액 열이 없다 — 마진 열이 붙을 자리가 없음');
  return { col: template.cols.length + 1, widthPx: 116, afterCol: colLetterToNumber(amountSpec.xlsxCol[1]) };
}

/** 채움 한 판의 불변 환경 — 조립 시작 때 한 번 만들어 모든 손에 쥐여준다(파라미터 3부류 중 '판'). */
interface FillEnv {
  template: PaperTemplate;
  specs: readonly ColumnSpec[];
  ctx: QuoteCtx;
  isCustomerView: boolean;
  /** 약식: 서버 구성부품을 펼치지 않고 서버 행 1개 + 문장 줄로. 정식(false)은 부품을 펼친다. */
  isBrief: boolean;
  /** 마진 금액 열 — 우리 뷰면 있고, 고객 뷰면 null(열 자체가 없다). */
  marginColumn: MarginColumn | null;
  /** 품목 행의 서식 출처(지도 조각) — 파트 itemTable / 서버 configSample 이 같은 모양. */
  itemRowSources: { itemStyleRow: number; partNumberLine: LineStyleSource; substitutionLine: LineStyleSource };
}

/** 파트 헤더 값 칸의 문구들 — 어느 칸에 어느 표제가 앉는지의 표. 빈 문구 칸은 채움이 틀 문구를 유지. */
function titleTexts(cells: TemplateMap['header']['valueCells'], title: QuoteTitle): Map<string, string | null> {
  return new Map([
    [cells.dealName, title.dealName],
    [cells.quoteNo, title.quoteNo],
    [cells.customerName, title.customerName ? `${title.customerName} 귀중` : null],
    [cells.issuedAt, formatDate(title.issuedAt)],
    [cells.validUntil, formatDate(title.validUntil)],
    [cells.contactName, title.contactName],
    [cells.contactPhone, title.contactPhone],
    [cells.contactEmail, title.contactEmail],
    [cells.assigneeName, title.assigneeName],
    [cells.assigneeEmail, title.assigneeEmail],
  ]);
}

/**
 * 품목표 머리 행의 판매 라벨 문구들 — 우리 뷰에는 매입 줄이 생기므로 '단가'→'판매단가' · '금액'→'판매금액 합계'로 정확히 한다.
 * 라벨의 정본은 열 명세(spec.title). 고객 뷰는 빈 표 — 틀 문구 그대로.
 */
function sellLabelTexts(env: FillEnv, itemTableHeadRow: number): Map<string, string> {
  if (env.isCustomerView) return new Map();
  const sellSpecs = env.specs.filter((spec) => spec.id === 'unitPrice' || spec.id === 'amount');
  return new Map(sellSpecs.map((spec) => [spec.xlsxCol[0] + itemTableHeadRow, spec.title]));
}

/** 푸터 값 칸의 문구들 — 어느 칸에 어느 합계가 앉는지의 표. 주소 열쇠는 틀 기준(밀림은 copyRegion 이 처리). */
function footerTexts(footer: TemplateMap['footer'], totals: Totals, itemCount: number): Map<string, string> {
  const cells = footer.valueCells;
  return new Map([
    [cells.itemsTotalLabel, footer.subtitleText(itemCount)],   // '품목 합계  (개별 파트 N종)'
    [cells.itemsTotal, formatWon(totals.net)],                 // 품목 합계 = 공급가액
    [cells.vat, formatWon(totals.vat)],
    [cells.total, formatWon(totals.total)],
  ]);
}

/** 서버 견적 채움. 합계 계산이 조립 앞에 오는 이유: SUMMARY 행 자체가 합계로 만들어진다. */
export function fillServerPaper(
  template: PaperTemplate,
  map: ServerTemplateMap,
  serverConfigs: ServerConfig[],
  extraParts: QuoteItem[],                       // 서버 구성 외 개별 파트(extraPartsOf) — 0개면 구역 없음
  ctx: QuoteCtx,
  isCustomerView = false,
  quoteTitle: QuoteTitle = NO_TITLE,             // 표제 — 없으면 헤더 표제 칸이 틀 문구 그대로
  isBrief = false,                               // 약식: 부품을 펼치지 않고 서버 행 + 문장 줄
): FilledPaper {
  assertServerMapCoversTemplate(map, template);                                  // 관문: 지도 정합
  const totals = totalsOfServerPaper(serverConfigs, extraParts, ctx);            // 계산: 구성들 + 개별 파트 합계 (데이터만으로)
  const specs = columnSpecs('server');
  const marginColumn = isCustomerView ? null : marginColumnOf(template, specs);  // 마진 열: 우리 뷰에만 (고객 뷰는 열 자체가 없다)
  const env: FillEnv = { template, specs, ctx, isCustomerView, isBrief, marginColumn, itemRowSources: map.configSample };

  // 조립 — 구역마다 한 문장, 위에서 아래로 (서버는 합계가 헤더에 앉는다).
  const header = copyRegion(template, map.header, map.header.fromRow,
    headerTexts(map.header, serverConfigs, totals, quoteTitle));
  const configsStart = map.header.toRow + 1;
  const configs = fillConfigRegions(env, map.configSample.head, serverConfigs, configsStart);
  const extraPartsStart = configsStart + spanOf(regionsOf(configs));
  const firstExtraPartIndex = itemCountOf(serverConfigs);                        // 편집 번호(data-idx)는 구성 품목들 뒤를 잇는다
  const extraPartsRegions = fillExtraPartsRegions(env, map, extraParts, firstExtraPartIndex, extraPartsStart);
  const configsAndExtraParts = [...configs, extraPartsRegions];                  // 같은 봉지 모양(머리 + 품목들), 지면 순서
  const summaryStart = configsStart + spanOf(regionsOf(configsAndExtraParts));
  const summary = fillSummaryRegion(template, map.summary, serverConfigs, extraParts, ctx, totals, summaryStart);
  const footer = copyRegion(template, map.footer, summaryStart + summary.rowHeights.length);

  const paperRegions = [header, ...regionsOf(configsAndExtraParts), summary, footer];
  return {
    inPaper: toInPaper(template, paperRegions, marginColumn),
    outPaper: { items: outPaperItemsOf(configsAndExtraParts), totals },
  };
}

/**
 * 서버 임대 견적 채움 — 하이브리드: 서버 구성 밴드(약식) 본문 + 파트식 SUMMARY 푸터.
 *   구성 밴드·서버 행은 fillServerPaper 의 조각을 그대로 쓴다(copyConfigHead 가 임대 문구로 갈린다).
 *   서버 임대는 정식(부품 펼침) 서식이 없다 — 언제나 약식(isBrief=true).
 *   P1: 우리 뷰 보강(마진 열·＋항목·소스 줄)은 없다 — env.isCustomerView 를 켜 보강을 통째 끈다(임대는 두 뷰가 같다).
 */
export function fillServerRentalPaper(
  template: PaperTemplate,
  map: ServerRentalTemplateMap,
  serverConfigs: ServerConfig[],
  extraParts: QuoteItem[],
  ctx: QuoteCtx,
  quoteTitle: QuoteTitle = NO_TITLE,
): FilledPaper {
  assertServerRentalMapCoversTemplate(map, template);                            // 관문: 지도 정합
  const totals = totalsOfRentalServerPaper(serverConfigs, extraParts);           // 계산: 구성들 + 개별 파트 임대 금액합계
  const specs = columnSpecs('server-rental');
  const env: FillEnv = { template, specs, ctx, isCustomerView: true, isBrief: true, marginColumn: null, itemRowSources: map.configSample };

  const header = copyRegion(template, map.header, map.header.fromRow,
    headerTexts(map.header, serverConfigs, totals, quoteTitle));
  const configsStart = map.header.toRow + 1;
  const configs = fillConfigRegions(env, map.configSample.head, serverConfigs, configsStart);   // 약식 서버 행 + 문장 줄, 밴드는 임대 문구
  const extraStart = configsStart + spanOf(regionsOf(configs));
  const firstExtraIndex = itemCountOf(serverConfigs);
  const extra = fillRentalExtraParts(env, map, extraParts, firstExtraIndex, extraStart);
  const configsAndExtra = [...configs, extra];
  const footerStart = configsStart + spanOf(regionsOf(configsAndExtra));
  const footer = copyRegion(template, map.footer, footerStart, footerTexts(map.footer, totals, serverConfigs.length));

  return {
    inPaper: toInPaper(template, [header, ...regionsOf(configsAndExtra), footer], null),
    outPaper: { items: outPaperItemsOf(configsAndExtra), totals },
  };
}

/**
 * 서버 임대 개별 파트 묶음 — 머리(띠 + 라벨 행) + 임대 품목 행들. fillExtraPartsRegions 의 임대판:
 *   소계 = Σ 금액합계(월임대 × 기간) · 품목 행 = 임대 열(우리 줄 없음). 0개면 빈 묶음.
 */
function fillRentalExtraParts(
  env: FillEnv, map: ServerRentalTemplateMap, extraParts: QuoteItem[],
  firstItemIndex: number, gridRowStart: number,
): ConfigRegions {
  if (extraParts.length === 0) return { head: { cells: [], rowHeights: [] }, items: [] };

  const [, nameCol] = parseAddress(map.configSample.head.valueCells.name);
  const [, subtotalCol] = parseAddress(map.configSample.head.valueCells.subtotal);
  const subtotal = extraParts.reduce((sum, item) => sum + (getRentalTotalKrw(item) ?? 0), 0);
  const band = fillRowDraft(env.template, map.extraParts.headStyleRow, new Map([
    [nameCol, EXTRA_PARTS_TITLE],
    [subtotalCol, formatWon(subtotal)],
  ]));
  const labels = copyRowDraft(env.template, map.extraParts.itemTableHeadRow);
  const head = toRegion([band, labels], gridRowStart);

  const items: FilledItem[] = [];
  let nextGridRowStart = gridRowStart + head.rowHeights.length;
  extraParts.forEach((part, partIndex) => {
    const filledItem = fillRentalItemRows(env, part, firstItemIndex + partIndex, nextGridRowStart);
    items.push(filledItem);
    nextGridRowStart += filledItem.inPaper.rowHeights.length;
  });
  return { head, items };
}

/** 구성 하나를 채운 결과 — 머리 구역(이름·칩·소계) + 채운 품목들(지면 안 구역 + 지면 밖 한 줄). 계보 그대로의 봉지.
 *  개별 파트 묶음도 같은 모양(머리 띠 + 품목들). */
interface ConfigRegions { head: Region; items: FilledItem[] }

/** 구성들의 품목 수 — 개별 파트의 편집 번호(data-idx)가 이어서 시작하는 자리. */
const itemCountOf = (serverConfigs: ServerConfig[]) => serverConfigs.reduce((count, config) => count + config.items.length, 0);

/** 구성들의 구역들 — 봉지를 풀어 지면 순서 나열로(구성마다 머리 → 품목들). 새 데이터가 아니라 모양 풀기. */
function regionsOf(configs: ConfigRegions[]): Region[] {
  return configs.flatMap(({ head, items }) => [head, ...items.map((filledItem) => filledItem.inPaper)]);
}

/** 묶음들의 지면 밖 줄들 — 채울 때 적어둔 것을 지면 순서대로 모은다(구성들 → 개별 파트). regionsOf 와 쌍. */
function outPaperItemsOf(configs: ConfigRegions[]): OutPaperItem[] {
  return configs.flatMap(({ items }) => items.map((filledItem) => filledItem.outPaper));
}

/** 구성들 — 구성마다 머리 copy + 품목 행들 fill. 품목 번호는 구성을 가로질러 이어진다(편집 data-idx 계약). */
function fillConfigRegions(
  env: FillEnv, head: ServerTemplateMap['configSample']['head'],
  serverConfigs: ServerConfig[], gridRowStart: number,
): ConfigRegions[] {
  const configRegions: ConfigRegions[] = [];
  let nextGridRowStart = gridRowStart;
  let flatItemIndex = 0;
  for (const config of serverConfigs) {
    const headRegion = copyConfigHead(env, head, config, nextGridRowStart);
    nextGridRowStart += headRegion.rowHeights.length;
    const items: FilledItem[] = [];
    if (env.isBrief) {
      // 약식: 서버 행 1개(섀시·컨디션·대수·1대가·금액) + 문장 줄(CPU:…/RAM:…) — 부품은 펼치지 않는다.
      const briefItem = fillServerBriefRows(env, config, flatItemIndex, nextGridRowStart);
      flatItemIndex++;
      items.push(briefItem);
      nextGridRowStart += briefItem.inPaper.rowHeights.length;
    } else {
      for (const item of config.items) {
        const filledItem = fillItemRows(env, item, flatItemIndex, nextGridRowStart);
        flatItemIndex++;
        items.push(filledItem);
        nextGridRowStart += filledItem.inPaper.rowHeights.length;
      }
    }
    configRegions.push({ head: headRegion, items });
  }
  return configRegions;
}

/** 서버 헤더 값 칸의 문구들 — 어느 칸에 어느 표제·합계가 앉는지의 표. 빈 문구 칸은 채움이 틀 문구를 유지. */
function headerTexts(
  header: ServerTemplateMap['header'], serverConfigs: ServerConfig[], totals: Totals, title: QuoteTitle,
): Map<string, string | null> {
  const unitCount = serverConfigs.reduce((sum, config) => sum + config.serverQty, 0);
  const cells = header.valueCells;
  return new Map([
    [cells.dealName, title.dealName],
    [cells.quoteNo, title.quoteNo],
    [cells.customerName, title.customerName ? `${title.customerName} 귀중` : null],
    [cells.issuedAt, formatDate(title.issuedAt)],
    [cells.validUntil, formatDate(title.validUntil)],
    [cells.contactName, title.contactName],
    [cells.contactPhone, title.contactPhone],
    [cells.contactEmail, title.contactEmail],
    [cells.assigneeName, title.assigneeName],
    [cells.assigneeEmail, title.assigneeEmail],
    [cells.total, formatWon(totals.total)],
    [cells.hangul, hangulAmountKrw(totals.total)],
    [cells.subtitle, header.subtitleText(serverConfigs.length, unitCount)],
  ]);
}

/** 구역들 → in-paper 지면. to~(모양 바꾸기). rows 필드는 직렬화 계약이라 이름 유지. */
function toInPaper(template: PaperTemplate, regions: Region[], marginColumn: MarginColumn | null): InPaper {
  // 마진 열은 금액 열 바로 뒤에 붙어 보인다 — 사이의 종이 여백 열은 접는다(너비 0). 칸 좌표는 하나도 안 민다.
  const cols = marginColumn
    ? [...template.cols.map((widthPx, colIndex) => (colIndex + 1 > marginColumn.afterCol ? 0 : widthPx)), marginColumn.widthPx]
    : template.cols;
  return {
    cols,
    rows: regions.flatMap((region) => region.rowHeights),
    styles: template.styles,
    cells: regions.flatMap((region) => region.cells),
  };
}

/* ═══════════════ ④ 구역 — 그대로 구역 · 구성 머리 · SUMMARY ═══════════════ */

/** 그대로 구역 — 틀의 구간(지도 조각 통째)을 행 초안으로 베껴 gridRowStart 부터 앉힌다. */
function copyRegion(
  template: PaperTemplate, zone: { fromRow: number; toRow: number }, gridRowStart: number,
  rowTexts?: ReadonlyMap<string, string | null>,   // 값 칸 문구 — 주소('AI20') → 문구 (빈 문구 = 틀 문구 유지)
): Region {
  const rows: RowDraft[] = [];
  for (let templateRowNumber = zone.fromRow; templateRowNumber <= zone.toRow; templateRowNumber++) {
    rows.push(copyRowDraft(template, templateRowNumber, rowTexts));
  }
  return toRegion(rows, gridRowStart);
}

/**
 * 구성 머리 — 구성 하나의 얼굴(번호·이름·칩·소계). 그대로 행의 반복판:
 * 같은 틀 구간을 구성마다 다시 베끼고, 구성 데이터로 만든 행 수준 값을 그 자리에서 앉힌다.
 */
function copyConfigHead(
  env: FillEnv, head: ServerTemplateMap['configSample']['head'],
  config: ServerConfig, gridRowStart: number,
): Region {
  const rentalTerm = config.rentalTermMonths;   // null 이면 판매 구성 — 소계·밴드 문구가 갈린다
  const texts = new Map<string, string>([
    [head.valueCells.index, String(config.index).padStart(2, '0')],
    [head.valueCells.name, config.name],
    // 소계 = 이 구성의 계약 총액. 판매: 1대가 × 대수 · 임대: 1대 월단가 × 대수 × 기간(금액합계).
    [head.valueCells.subtotal, formatWon(rentalTerm != null ? config.unitPrice * config.serverQty * rentalTerm : config.unitPrice * config.serverQty)],
    [head.valueCells.specLine, config.specLine],
    // 밴드머리 문구 — 판매: '수량 N대 × 1대 ₩X' · 임대: 'N개월 × N대 × 월 ₩X'.
    [head.valueCells.qtyLine, rentalTerm != null
      ? rentalBandSubtitle(rentalTerm, config.serverQty, config.unitPrice)
      : `수량 ${config.serverQty}대   ×   1대 ₩${formatWon(config.unitPrice)}`],
    ...sellLabelTexts(env, head.itemTableHeadRow),                       // 품목표 머리 행은 구성 머리 안에 있다(임대는 빈 표)
  ]);
  head.chips.forEach((slot, slotIndex) => {
    const chip = config.chips[slotIndex];
    texts.set(slot.label, chip?.label ?? '');
    texts.set(slot.value, chip?.value ?? '');
    if (slot.detail) texts.set(slot.detail, chip?.detail ?? '');
  });
  let headRegion = copyRegion(env.template, head, gridRowStart, texts);
  headRegion = appendMarginColumnHead(headRegion, env, head, gridRowStart);            // 우리 뷰: 머리 행 끝에 '마진 금액'
  headRegion = appendAddItemsButton(headRegion, env, head, config, gridRowStart);      // 우리 뷰: 아래 칩 줄 오른쪽 끝 빈자리에 '＋ 항목'
  return headRegion;
}

/** "＋ 항목" 버튼 칸이 차지하는 열 수 — 아래 칩 줄(RAID·NIC·PSU)의 오른쪽 끝, 판매금액 합계 열 끝에 맞춰 4열(약 107px). */
const ADD_ITEMS_BUTTON_COLS = 4;

/**
 * 구성 머리에 "＋ 항목" 버튼 — 아래 칩 줄(마지막 칩이 있는 행)의 오른쪽 끝 빈자리에 얹은 우리 칸
 * (클릭 → 그 서버에 품목 추가 모달)을 덧붙인 구역을 돌려준다. 오른쪽 끝 = 마진 열 바로 앞(판매금액 합계 열 끝).
 * 이름 줄 오른쪽 끝은 탈락 — 이름 문구와 소계 사이에 끼어 제목을 가렸다("아래 빈자리가 훨씬 낫다").
 * 고객 뷰(우리 칸 없음)면 받은 구역 그대로.
 */
function appendAddItemsButton(
  region: Region, env: FillEnv,
  head: ServerTemplateMap['configSample']['head'], config: ServerConfig,
  gridRowStart: number,
): Region {
  if (env.isCustomerView) return region;
  assert(env.marginColumn, '우리 뷰인데 마진 열이 없다 — 버튼의 오른쪽 끝을 정할 수 없음');

  const lastChip = head.chips[head.chips.length - 1];
  const [chipRow] = parseAddress(lastChip.value);
  const buttonCol = env.marginColumn.afterCol - ADD_ITEMS_BUTTON_COLS + 1;
  const isVacant = cellsAtRow(env.template, chipRow).every((cell) => cell.c + cell.cs <= buttonCol);
  assert(isVacant, `칩 줄 ${chipRow}행의 ${buttonCol}열부터가 비어 있지 않다 — 버튼이 틀 칸을 덮는다`);
  const chipGridRow = gridRowStart + (chipRow - head.fromRow);
  const button = makeAddItemsButton(config, chipGridRow, buttonCol);

  return { ...region, cells: [...region.cells, button] };
}

/** "＋ 항목" 버튼 칸 하나 — 서버 quote_item id 를 짐으로 든다(모달이 그 서버를 미리 고른다). */
function makeAddItemsButton(config: ServerConfig, gridRow: number, col: number): FilledCell {
  assert(config.serverId != null, `서버 ${config.index} 는 아직 저장 안 된 행 — 품목을 붙일 수 없다`);
  return { r: gridRow, c: col, rs: 1, cs: ADD_ITEMS_BUTTON_COLS, s: 0, t: '＋ 항목', kind: 'our-line', look: 'add-items', addItemsServerId: config.serverId };
}

/** 마진 열 머리 — 품목표 머리 행 끝에 '마진 금액' 칸을 덧붙인 구역을 돌려준다(우리 뷰). 고객 뷰(마진 열 없음)면 받은 구역 그대로. */
function appendMarginColumnHead(
  region: Region, env: FillEnv,
  zone: { fromRow: number; itemTableHeadRow: number },   // 머리 행이 든 지도 조각
  gridRowStart: number,                                  // 그 구역이 앉은 지면 시작 행
): Region {
  if (!env.marginColumn) return region;

  const headGridRow = gridRowStart + (zone.itemTableHeadRow - zone.fromRow);   // 머리 행이 앉은 지면 행 = 구역 시작 + 원산지 오프셋
  const marginHead = makeMarginColumnHead(env.template, env.marginColumn, zone.itemTableHeadRow, headGridRow);

  return { ...region, cells: [...region.cells, marginHead] };
}

/**
 * '마진 금액' 머리 칸 — 틀 밖 칸이라 무에서 만들되, 옷(s)은 금액 머리 칸에게 빌린다(같은 줄의 머리처럼 보이게).
 * 금액 머리 칸 = 머리 행에서 마진 열 바로 앞(afterCol)에서 끝나는 칸.
 */
function makeMarginColumnHead(
  template: PaperTemplate, marginColumn: MarginColumn,
  itemTableHeadRow: number,   // 원산지 틀 행 — 옷을 빌릴 곳
  gridRow: number,            // 앉을 지면 행
): FilledCell {
  const amountHead = cellsAtRow(template, itemTableHeadRow).find((cell) => cell.c + cell.cs - 1 === marginColumn.afterCol);
  assert(amountHead, `품목표 머리 행 ${itemTableHeadRow}에 마진 열 앞 금액 머리 칸이 없다`);

  return { r: gridRow, c: marginColumn.col, rs: 1, cs: 1, s: amountHead.s, t: '마진 금액' };
}

/** 개별 파트 머리 띠 문구 — 구형 문서의 구분행 문구를 잇는다(디자인은 v3 구성 머리 띠). */
const EXTRA_PARTS_TITLE = '개별 파트 · Add Option Parts';

/**
 * 개별 파트 묶음 — 머리(띠 + 라벨 행) + 품목들. 구성과 같은 봉지 모양(ConfigRegions). 0개면 빈 묶음(행 0).
 *   띠: 구성 머리 첫 줄의 옷(fill — 번호 '01' 잔상 차단), 이름 칸엔 제목, 소계 칸엔 개별 파트 합.
 *   라벨 행: 품목표 머리 행 copy(우리 뷰는 판매 라벨 + '마진 금액' 머리).
 *   품목 행들: 구성 부품과 같은 행 타입(값행·품번 줄·대체 줄·우리 줄). 편집 번호는 구성 품목들 뒤를 잇는다.
 */
function fillExtraPartsRegions(
  env: FillEnv, map: ServerTemplateMap, extraParts: QuoteItem[],
  firstItemIndex: number, gridRowStart: number,
): ConfigRegions {
  if (extraParts.length === 0) return { head: { cells: [], rowHeights: [] }, items: [] };

  const [, nameCol] = parseAddress(map.configSample.head.valueCells.name);
  const [, subtotalCol] = parseAddress(map.configSample.head.valueCells.subtotal);
  const subtotal = totalsOfItems(extraParts, env.ctx).net;
  const band = fillRowDraft(env.template, map.extraParts.headStyleRow, new Map([
    [nameCol, EXTRA_PARTS_TITLE],
    [subtotalCol, formatWon(subtotal)],
  ]));
  const labels = copyRowDraft(env.template, map.extraParts.itemTableHeadRow, sellLabelTexts(env, map.extraParts.itemTableHeadRow));
  let head = toRegion([band, labels], gridRowStart);
  head = appendMarginColumnHead(head, env,
    { fromRow: map.extraParts.itemTableHeadRow - 1, itemTableHeadRow: map.extraParts.itemTableHeadRow },   // 띠가 라벨 행 바로 위 한 줄 → 원산지 오프셋 1
    gridRowStart);

  const items: FilledItem[] = [];
  let nextGridRowStart = gridRowStart + head.rowHeights.length;
  extraParts.forEach((part, partIndex) => {
    const filledItem = fillItemRows(env, part, firstItemIndex + partIndex, nextGridRowStart);
    items.push(filledItem);
    nextGridRowStart += filledItem.inPaper.rowHeights.length;
  });
  return { head, items };
}

/** SUMMARY 한 줄의 재료 — 라벨·값·서식 원본 행. */
interface SummaryLine { label: string; value: number; styleRowNumber: number }

/** SUMMARY 구역 — 머리행 copy → 줄들(좌 TERMS | 우 돈 요약) × N → 끝행 copy. 개별 파트 소계 줄은 개별 파트가 있을 때만. */
function fillSummaryRegion(
  template: PaperTemplate, summary: ServerTemplateMap['summary'],
  serverConfigs: ServerConfig[], extraParts: QuoteItem[], ctx: QuoteCtx, totals: Totals, gridRowStart: number,
): Region {
  const extraPartsLine: SummaryLine[] = extraParts.length > 0
    ? [{ label: '개별 파트 소계', value: totalsOfItems(extraParts, ctx).net, styleRowNumber: summary.subtotalRow }]
    : [];
  const summaryLines: SummaryLine[] = [
    ...serverConfigs.map((config, configIndex) => ({
      label: `서버 ${String(configIndex + 1).padStart(2, '0')} 소계  (×${config.serverQty}대)`,
      value: config.unitPrice * config.serverQty, styleRowNumber: summary.subtotalRow,
    })),
    ...extraPartsLine,
    { label: '공급가액 (Net)', value: totals.net, styleRowNumber: summary.netRow },
    { label: '부가세 VAT 10%', value: totals.vat, styleRowNumber: summary.vatRow },
    { label: '합 계 (Total)', value: totals.total, styleRowNumber: summary.totalRow },
  ];
  const lineCount = Math.max(summary.termsRows.length, summaryLines.length);
  const rows: RowDraft[] = [
    copyRowDraft(template, summary.headerRow),
    ...Array.from({ length: lineCount }, (_, lineIndex) =>
      fillSummaryRow(template, summary, summary.termsRows[lineIndex], summaryLines[lineIndex])),
    copyRowDraft(template, summary.noteRow),
  ];
  return toRegion(rows, gridRowStart);
}

/** SUMMARY 한 줄 — 한 행의 좌우가 두 세계: 좌측(labelCol 앞) TERMS 는 copy, 우측은 이번 견적의 돈 요약을 fill. */
function fillSummaryRow(
  template: PaperTemplate, summary: ServerTemplateMap['summary'],
  termsRowNumber: number | undefined, summaryLine: SummaryLine | undefined,
): RowDraft {
  const labelCol = colLetterToNumber(summary.labelCol);
  const valueCol = colLetterToNumber(summary.valueCol);
  const styleRowNumber = summaryLine ? summaryLine.styleRowNumber : (termsRowNumber ?? summary.subtotalRow);
  const cells: FilledCell[] = [];
  if (termsRowNumber != null) {   // 좌측 — 거래조건은 문서 그대로
    for (const templateCell of cellsAtRow(template, termsRowNumber)) {
      if (templateCell.c < labelCol) cells.push({ ...templateCell });
    }
  }
  if (summaryLine != null) {   // 우측 — 이번 견적의 돈 요약을 새로
    for (const templateCell of cellsAtRow(template, styleRowNumber)) {
      if (templateCell.c < labelCol) continue;
      let text = templateCell.t;
      if (templateCell.c === labelCol) text = summaryLine.label;
      else if (templateCell.c === valueCol) text = formatWon(summaryLine.value);
      cells.push({ ...templateCell, ...(text != null ? { t: text } : {}) });
    }
  }
  return { cells, rowHeight: rowHeightOf(template, styleRowNumber) };
}

/* ═══════════════ ⑤ 품목 묶음 — 값행+딸린 줄들 (파트·서버 공용: 열 명세가 같은 모양이라 함수도 하나) ═══════════════ */

/**
 * 품목의 행들 — QuoteItem 하나를 행 2~4개로 앉힌다. 문구의 주인은 우리(열 명세+데이터), 틀은 옷만.
 *   값행(열 명세대로 값·편집 칸) → 우리 줄(우리 뷰) → 품번 줄 → 대체 줄(우리 뷰).
 * 우리 줄이 값행 바로 다음인 이유: 품목을 닫는 구분선은 틀의 품번 줄 옷(아랫선)에 박혀 있다 —
 *   우리 줄을 품번 줄 뒤에 두면 선 아래로 밀려 다음 품목 것처럼 보인다. 값행 바로 밑이라 매입 칸도 판매단가 짝 밑에 앉는다.
 * 지면 밖 한 줄(어느 행부터 몇 줄)도 여기서 같이 적는다 — 어디에 몇 줄로 앉았는지는 이 순간이 제일 잘 안다.
 */
function fillItemRows(env: FillEnv, item: QuoteItem, itemIndex: number, gridRowStart: number): FilledItem {
  const rows = [
    fillValueRow(env, item, itemIndex),                                                                  // 값행 — 열 명세대로 값·편집 칸
    env.isCustomerView ? null : fillOurLineRow(env, item, itemIndex),                                    // 우리 줄 — 우리 뷰만, 값행 바로 밑
    fillLineRow(env, env.itemRowSources.partNumberLine, partNumberLineText(item)),                       // 품번 줄 — 고객에게 실림, 아랫선이 품목을 닫는다
    env.isCustomerView ? null : fillLineRow(env, env.itemRowSources.substitutionLine, substitutionLineText(item)), // 대체 줄 — 우리 뷰만
  ].filter((row): row is RowDraft => row != null);
  const inPaper = toRegion(rows, gridRowStart);
  return { inPaper, outPaper: { itemIndex, id: item.id, gridRowStart, span: inPaper.rowHeights.length } };
}

/** 약식 서버 품목 — 서버 행(값행) + 문장 줄들. 구성부품을 펼치는 대신 한 덩어리로 요약한다.
 *  서버 행: 구분 'Server' · 구성파트 = 섀시 문장(문장 줄 첫 줄) · 컨디션 = 서버 컨디션 · 수량 = 대수 · 단가 = 1대가.
 *  문장 줄: buildServerDescription 의 둘째 줄부터(CPU:…/RAM:…) — 품번 줄 서식으로 앉힌다. */
function fillServerBriefRows(env: FillEnv, config: ServerConfig, itemIndex: number, gridRowStart: number): FilledItem {
  const serverRow = fillValueRow(env, briefServerRowItem(config), itemIndex);
  const sentenceRows = config.briefLines.slice(1)
    .map((line) => fillLineRow(env, env.itemRowSources.partNumberLine, line))
    .filter((row): row is RowDraft => row != null);
  const inPaper = toRegion([serverRow, ...sentenceRows], gridRowStart);
  return { inPaper, outPaper: { itemIndex, id: config.serverId, gridRowStart, span: inPaper.rowHeights.length } };
}

/** 약식 서버 행이 값행 채움에 넘길 합성 품목 — 서버 한 덩어리를 품목 한 줄처럼 보이게 한다(부품 아님). */
function briefServerRowItem(config: ServerConfig): QuoteItem {
  return {
    id: config.serverId, category: 'Server', optionPart: null, requestedPart: null, condition: config.condition,
    sparePart: null, assemblyPart: null, quantity: config.serverQty, unitPriceUsd: null, unitPriceKrw: config.unitPrice,
    isBasePart: false, isUnregistered: false, parentId: null, dealItemId: null, parentDealItemId: null, lineType: 'server',
    costUsd: null, costKrw: null, serverQuantity: config.serverQty, unitPriceOverride: config.unitPrice,
    description: config.name,   // 서버 행 구성파트 = 모델명(섀시 문장 줄은 slice(1)로 이미 빠진다)
    sourceType: null, sourceRefId: null, sourceName: null, sourceCondition: null,
    rentalTermMonths: config.rentalTermMonths, rentalContractCostKrw: config.rentalContractCostKrw,   // 임대면 기간·총원가(판매면 null)
  };
}

/** 값행 — 열 명세가 "이 열엔 이 값·이 편집기"를 정한다. 우리 뷰는 끝에 마진 금액 칸(틀 밖 열)이 붙는다. */
function fillValueRow(env: FillEnv, item: QuoteItem, itemIndex: number): RowDraft {
  const { specs, ctx } = env;
  const textByCol = new Map<number, string>();
  const editsByCol = new Map<number, EditStamp>();
  for (const spec of specs) {
    const col = colLetterToNumber(spec.xlsxCol[0]);
    const cellValue = spec.value ? spec.value(item, ctx, itemIndex, env.isCustomerView) : spec.field != null ? item[spec.field] : undefined;
    textByCol.set(col, formatValue(cellValue, spec.numFmt));
    const editTargetField = spec.editField ?? spec.field;
    if (spec.edit !== 'derived' && editTargetField) {
      editsByCol.set(col, { idx: itemIndex, field: editTargetField, input: spec.edit === 'number' ? 'number' : spec.edit === 'picked' ? 'picked' : 'text' });
    }
  }
  const valueRow = fillRowDraft(env.template, env.itemRowSources.itemStyleRow, textByCol, editsByCol);
  if (!env.marginColumn) return valueRow;                                          // 고객 뷰: 마진 열이 없다

  const marginAmount = makeMarginAmountCell(item, ctx, env.itemRowSources.itemStyleRow, env.marginColumn);
  return { ...valueRow, cells: [...valueRow.cells, marginAmount] };
}

/** 덧붙임 줄(품번·대체) — 서식 원본 옷 + 문구 한 칸. 문구 없으면 줄 없음(null). 문구 정본은 paper-1. */
function fillLineRow(env: FillEnv, source: LineStyleSource, text: string | null): RowDraft | null {
  if (text == null) return null;
  return fillRowDraft(env.template, source.styleRow, new Map([[colLetterToNumber(source.textStartCol), text]]));
}

/**
 * 우리 줄 — 값행 옷의 빈 행 위에 우리 4칸을 짝 자리에 덮어 앉힌다. 명세가 없으면 줄 없음.
 *   빈 행을 까는 이유: 서버 틀의 왼쪽 세로 막대(3열 왼선)처럼 값행이 입은 옷이 우리 줄에서도 끊기지 않게.
 *   우리 칸의 자리는 열 명세(part·unitPrice·amount)의 범위 + 마진 열 — 값행 칸과 시작 열·너비가 같으니 그 칸을 갈아 끼운다.
 */
function fillOurLineRow(env: FillEnv, item: QuoteItem, itemIndex: number): RowDraft | null {
  const { specs, ctx, template, itemRowSources, marginColumn } = env;
  assert(marginColumn, '우리 줄은 우리 뷰에만 — 마진 열이 있어야 한다');
  const partSpec = specs.find((spec) => spec.id === 'part');
  const priceSpec = specs.find((spec) => spec.id === 'unitPrice');
  const amountSpec = specs.find((spec) => spec.id === 'amount');
  if (!partSpec || !priceSpec || !amountSpec) return null;
  const ourCells = makeOurLineCells(item, ctx, itemIndex, itemRowSources.itemStyleRow, {
    source: colSpanOf(partSpec.xlsxCol), costUnit: colSpanOf(priceSpec.xlsxCol),
    costAmount: colSpanOf(amountSpec.xlsxCol), marginPercent: [marginColumn.col, 1],
  });
  const blankValueRow = fillRowDraft(template, itemRowSources.itemStyleRow);
  const isReplacedByOurCell = (cell: FilledCell) => ourCells.some((ourCell) => ourCell.c === cell.c);
  const isOutsideOurCells = (cell: FilledCell) => ourCells.every((ourCell) => cell.c + cell.cs <= ourCell.c || ourCell.c + ourCell.cs <= cell.c);
  assert(blankValueRow.cells.every((cell) => isReplacedByOurCell(cell) || isOutsideOurCells(cell)),
    '우리 칸이 값행 칸과 어긋나게 겹친다 — 열 명세 범위와 틀의 값행 칸 경계가 같아야 한다');
  const cells = [...blankValueRow.cells.filter((cell) => !isReplacedByOurCell(cell)), ...ourCells];
  return { cells, rowHeight: blankValueRow.rowHeight };
}

/* ═══════════════ ⑥ 행 재료 — 행 초안(RowDraft)을 만드는 3가지 방법 + 앉히기 ═══════════════ */

const cellsAtRow = (template: PaperTemplate, templateRowNumber: number) => template.cells.filter((cell) => cell.r === templateRowNumber);
const rowHeightOf = (template: PaperTemplate, templateRowNumber: number) => template.rows[templateRowNumber - 1];

/** 행 초안들 → 구역 — 시작 행부터 차례로 앉힌다. 위치는 나열 순서가 말한다. */
function toRegion(rows: RowDraft[], gridRowStart: number): Region {
  return {
    cells: rows.flatMap((row, rowIndex) => row.cells.map((cell) => ({ ...cell, r: gridRowStart + rowIndex }))),
    rowHeights: rows.map((row) => row.rowHeight),
  };
}

/** copy — 틀의 행 그대로(문구가 곧 문서). rowTexts 에 주소가 있으면 그 칸만 값으로.
 *  빈 문구(null·'')는 앉히지 않는다 — 그 칸은 틀 문구 유지 (fillRowDraft 와 같은 규칙). */
function copyRowDraft(template: PaperTemplate, templateRowNumber: number, rowTexts?: ReadonlyMap<string, string | null>): RowDraft {
  const cells = cellsAtRow(template, templateRowNumber).map((cell) => {
    const text = rowTexts?.get(colNumberToLetter(cell.c) + cell.r);
    return text ? { ...cell, t: text } : { ...cell };
  });
  return { cells, rowHeight: rowHeightOf(template, templateRowNumber) };
}

/**
 * fill — 틀의 행에서 '서식만' 차용해 새 행 초안을 만든다. 시작은 빈 종이:
 * 틀 문구는 절대 상속하지 않는다(틀엔 옛 견적 'HDD'·'650,000' 이 박제되어 있다 — 잔상 차단).
 * 실리는 문구는 textByCol 이 준 것뿐. editsByCol 이 있으면 그 칸을 편집 칸으로 승격.
 */
function fillRowDraft(
  template: PaperTemplate, styleRowNumber: number,
  textByCol?: Map<number, string>, editsByCol?: Map<number, EditStamp>,
): RowDraft {
  const cells = cellsAtRow(template, styleRowNumber).map((styleCell): FilledCell => {
    const text = textByCol?.get(styleCell.c);
    const edit = editsByCol?.get(styleCell.c);
    const blankCell: TemplateCell = { ...styleCell };
    delete blankCell.t;                                  // 빈 종이 — 잔상 상속 금지
    if (text != null && text !== '') blankCell.t = text;
    return edit ? { ...blankCell, kind: 'edit', ...edit } : blankCell;
  });
  return { cells, rowHeight: rowHeightOf(template, styleRowNumber) };
}

/**
 * 우리 줄(in-paper) 4칸 — 소스 · 매입단가 · 매입금액 합계 · 마진율. 틀에 없는 줄이라 무에서 만든다. 파트·서버 공용.
 * 각 칸은 바로 위 값행의 짝 밑에 앉는다(fillItemRows 가 값행 바로 다음에 놓는다): 매입단가는 판매단가 밑, 매입 합계는 판매금액 밑, 마진율은 마진 금액 밑.
 * 마진율 색 문턱의 정본(20%↑ 초록 · 12%↑ 호박 · 그 밑 빨강 · 계산불가 연회색).
 * 고객 뷰에서는 아예 안 만든다. (지면 밖 out-paper 세 겹은 render-our-view-out-paper.)
 */
function makeOurLineCells(
  item: QuoteItem, ctx: QuoteCtx, itemIndex: number,
  styleRowNumber: number,   // 초안 좌표(서식 원본 행) — toRegion 이 지면 행으로 앉힌다
  cols: {                   // 자리 이름은 '무엇이 앉나'
    source: [start: number, span: number]; costUnit: [number, number]; costAmount: [number, number]; marginPercent: [number, number];
  },
): FilledCell[] {
  const costUnit = costUnitKrw(item, ctx), costAmount = costAmountKrw(item, ctx), percent = marginPercent(item, ctx);
  const marginLook = percent == null ? 'margin-none' as const : percent >= 20 ? 'margin-good' as const : percent >= 12 ? 'margin-mid' as const : 'margin-low' as const;
  const sourceInfo = sourceLabel(item, ctx);
  return [
    { r: styleRowNumber, c: cols.source[0], rs: 1, cs: cols.source[1], s: 0, t: sourceInfo.text, kind: 'our-line', look: sourceInfo.isLinked ? 'source-linked' : 'source-missing', sourceIdx: itemIndex },
    // 매입 단가 칸은 ⓘ 버튼이기도 하다 — 클릭하면 이 품목의 매입 후보 패널(판매단가 바로 아래라 자리도 맞고, 뜻도 매입)
    { r: styleRowNumber, c: cols.costUnit[0], rs: 1, cs: cols.costUnit[1], s: 0, t: costUnit != null ? `ⓘ 매입 ${formatWon(costUnit)}` : 'ⓘ 매입 —', kind: 'our-line', look: 'cost', costHintIdx: itemIndex },
    { r: styleRowNumber, c: cols.costAmount[0], rs: 1, cs: cols.costAmount[1], s: 0, t: costAmount != null ? formatWon(costAmount) : '—', kind: 'our-line', look: 'cost' },
    { r: styleRowNumber, c: cols.marginPercent[0], rs: 1, cs: cols.marginPercent[1], s: 0, t: percent != null ? `${percent}%` : '—', kind: 'our-line', look: marginLook },
  ];
}

/** 값행의 마진 금액 칸 — 마진 열(틀 밖)에 앉는 우리 전용 칸. 부호가 look 을 가른다(양수 초록 · 음수 빨강 · 계산불가 회색 '—'). */
function makeMarginAmountCell(item: QuoteItem, ctx: QuoteCtx, styleRowNumber: number, marginColumn: MarginColumn): FilledCell {
  const margin = marginKrw(item, ctx);
  const look = margin == null ? 'margin-amount-none' as const : margin >= 0 ? 'margin-amount-plus' as const : 'margin-amount-minus' as const;
  const text = margin == null ? '—' : `${margin >= 0 ? '+' : ''}${formatWon(margin)}`;
  return { r: styleRowNumber, c: marginColumn.col, rs: 1, cs: 1, s: 0, t: text, kind: 'our-line', look };
}

const colSpanOf = (range: readonly [string, string]): [number, number] =>
  [colLetterToNumber(range[0]), colLetterToNumber(range[1]) - colLetterToNumber(range[0]) + 1];

/* ═══════════════ ⑦ 값 — 합계 계산과 문구 서식 ═══════════════ */

/** 합계 셋 — 파트: 품목 판매금액의 합. (원 단위 일치는 골든 테스트 책임) */
function totalsOfItems(quoteItems: QuoteItem[], ctx: QuoteCtx): Totals {
  const net = quoteItems.reduce((sum, item) => sum + (sellAmountKrw(item, ctx) ?? 0), 0);
  const vat = Math.round(net * 0.1);
  return { net, vat, total: net + vat };
}

/** 합계 셋 — 임대: 품목 금액합계(월 임대금액 × 기간)의 합. */
function totalsOfRentalItems(quoteItems: QuoteItem[]): Totals {
  const net = quoteItems.reduce((sum, item) => sum + (getRentalTotalKrw(item) ?? 0), 0);
  const vat = Math.round(net * 0.1);
  return { net, vat, total: net + vat };
}

/** 합계 셋 — 서버 지면: 구성별 (1대 단가 × 대수)의 합 + 개별 파트 판매금액의 합. */
function totalsOfServerPaper(serverConfigs: ServerConfig[], extraParts: QuoteItem[], ctx: QuoteCtx): Totals {
  const configsNet = serverConfigs.reduce((sum, config) => sum + config.unitPrice * config.serverQty, 0);
  const net = configsNet + totalsOfItems(extraParts, ctx).net;
  const vat = Math.round(net * 0.1);
  return { net, vat, total: net + vat };
}

/** 합계 셋 — 서버 임대 지면: 구성별 금액합계(월단가 × 대수 × 기간)의 합 + 개별 파트 임대 금액합계의 합. */
function totalsOfRentalServerPaper(serverConfigs: ServerConfig[], extraParts: QuoteItem[]): Totals {
  const configsNet = serverConfigs.reduce((sum, config) => sum + config.unitPrice * config.serverQty * (config.rentalTermMonths ?? 0), 0);
  const extraNet = extraParts.reduce((sum, item) => sum + (getRentalTotalKrw(item) ?? 0), 0);
  const net = configsNet + extraNet;
  const vat = Math.round(net * 0.1);
  return { net, vat, total: net + vat };
}

/** 원화 금액 → 한글(일금 …원정). 웹과 xlsx 가 같은 값을 실어야 하므로 이 함수가 정본. */
export function hangulAmountKrw(amountKrw: number): string {
  const num = Math.floor(Math.abs(amountKrw));
  if (num === 0) return '일금 영원정';
  const DIGIT = '영일이삼사오육칠팔구', PLACE = ['', '십', '백', '천'], GROUP = ['', '만', '억', '조', '경'];
  let result = '', remaining = num, groupIndex = 0;
  while (remaining > 0) {
    const group = remaining % 10000; remaining = Math.floor(remaining / 10000);
    if (group) {
      let groupText = '', value = group, place = 0;
      while (value > 0) { const digit = value % 10; if (digit) groupText = DIGIT[digit] + PLACE[place] + groupText; value = Math.floor(value / 10); place++; }
      result = groupText + GROUP[groupIndex] + result;
    }
    groupIndex++;
  }
  return `일금 ${result}원정`;
}

const formatWon = (amount: number) => amount.toLocaleString('ko-KR');

/** ISO 날짜 문구 → 점 표기. '2026-09-01T…' → '2026.09.01' · null → ''. */
const formatDate = (dateText: string | null): string => (dateText ? dateText.slice(0, 10).replace(/-/g, '.') : '');

/** 스펙 값 → 셀 문구. numFmt '#,##0' 이면 천단위, 아니면 String. */
function formatValue(value: number | string | boolean | null | undefined, numFmt?: string): string {
  if (value == null) return '';
  if (typeof value === 'number') return numFmt && /#,##0/.test(numFmt) ? value.toLocaleString('ko-KR') : String(value);
  return String(value);
}

/* ═══════════════ ⑧ 어댑터 — 납작한 편집 품목들 → 서버 구성들 ═══════════════ */

/**
 * 서버 구성 한 벌 — 지면의 파란 덩어리 하나. 코드베이스 전역의 그 '서버 구성'
 * (ServerConfigView·server-config-api 의 딜 서버 서브트리)이 견적 지면에 실린 모습.
 */
export interface ServerConfig {
  index: number; name: string; specLine: string;
  serverId: number | null;                    // 서버 행 quote_item id — 품목 추가의 부모(parent_quote_item_id)
  serverQty: number; unitPrice: number;       // 1대 단가(override ?? Σ부품)
  condition: string | null;                   // 서버 컨디션(한글 3그룹) — 약식 서버 행에 쓴다
  chips: Array<{ label: string; value: string; detail?: string }>;
  items: QuoteItem[];                          // 구성부품(1대 기준 수량)
  briefLines: string[];                        // 약식 문장 줄 [섀시, 'CPU : …', …] — buildServerDescription
  // ── 임대(서버 임대 지면만) — 판매 구성은 null. unitPrice 가 곧 1대 월 임대단가다. ──
  rentalTermMonths: number | null;             // 임대 기간(개월) — 서버 헤더행 rental_term_months
  rentalContractCostKrw: number | null;        // 라인 임대총원가(우리 뷰 마진, 후속)
}

/** 칩 라벨 표기(카테고리 → 견적서 칩 라벨). */
const CHIP_LABEL: Record<string, string> = { NETWORK: 'NIC', POWER: 'PSU' };
/** 스펙 값 정리 — 표시용 축약(선두 'HPE ' 제거). 정식 스펙 집계(총 메모리 등)는 후속. */
const shortSpec = (description: string | null): string => {
  const trimmed = (description ?? '').replace(/^HPE\s+/i, '').trim();
  if (trimmed.length <= 16) return trimmed;
  const cut = trimmed.slice(0, 18); const spaceIndex = cut.lastIndexOf(' ');
  return (spaceIndex > 8 ? cut.slice(0, spaceIndex) : cut).trim();
};

/**
 * 납작한 편집 데이터(QuoteItem[]) → 서버 구성들. toQuoteItem 가족(모양 바꾸기).
 *   서버행 = lineType==='server' · 섀시(category='chassis')=구성 이름.
 *   부품 → 서버 링크는 둘 — 견적이 어디서 왔느냐로 갈린다(둘 다 인정):
 *     딜에서 만든 견적  : parentDealItemId === 서버.dealItemId (딜 구성 트리를 옮긴 정본 링크)
 *     지면에서 추가한 부품: parentId === 서버.id           (deal_item 이 없어 quote_item 끼리 직접 링크)
 *   구성 안 부품 순서 = 서버 구성 페이지와 같은 규칙: 카테고리 정본 순서(SPEC_CATEGORIES), 같은 카테고리는 추가 순서(sort_order).
 *   지면에서 추가한 부품이 맨 아래에 붙지 않고 제 카테고리 자리로 들어가는 이유가 이 정렬이다.
 *   단가 = unitPriceOverride ?? Σ(판매단가×수량) · 소계 = 단가×serverQuantity.
 * fill 안에 못 들어가는 이유: 산출물의 items 순서가 편집 레이어(items·data-idx)와
 * 지면을 묶는 계약이라, View 도 이 결과를 직접 쥐어야 한다(refill 참조).
 */
/** 부품의 카테고리 정본 순위 — 딜 구성은 견적 어휘(CHASSIS·RAM), 자립·추가 행은 카탈로그 어휘(chassis·memory)라 먼저 정규화. 미지 카테고리는 맨 뒤. */
const categoryRank = (part: QuoteItem) => specCategoryOrder(toSpecCategory(part.category));

/** 부품 → 서버 링크 둘(견적 출신에 따라 하나만 있다): 지면에서 추가한 부품은 quote_item 끼리, 딜에서 만든 견적은 deal_item 으로. */
const isLinkedByQuoteItem = (part: QuoteItem, server: QuoteItem) => part.parentId != null && part.parentId === server.id;
const isLinkedByDealItem = (part: QuoteItem, server: QuoteItem) => part.parentDealItemId != null && part.parentDealItemId === server.dealItemId;
const isPartOfServer = (part: QuoteItem, server: QuoteItem) => part.lineType !== 'server' && (isLinkedByQuoteItem(part, server) || isLinkedByDealItem(part, server));

/** 개별 파트 — 서버 행도 아니고 어느 서버에도 안 묶인 부품(구형 문서의 "Add Option Parts"). 카테고리 정본 순서. */
export function extraPartsOf(items: QuoteItem[]): QuoteItem[] {
  const servers = items.filter((item) => item.lineType === 'server');
  return items
    .filter((item) => item.lineType !== 'server' && !servers.some((server) => isPartOfServer(item, server)))
    .sort((left, right) => categoryRank(left) - categoryRank(right));
}

export function toServerConfigs(items: QuoteItem[], ctx: QuoteCtx): ServerConfig[] {
  const servers = items.filter((item) => item.lineType === 'server');
  const componentsOf = (server: QuoteItem) =>
    items.filter((item) => isPartOfServer(item, server))
      .sort((left, right) => categoryRank(left) - categoryRank(right));   // 안정 정렬 — 같은 카테고리는 sort_order 유지
  return servers.map((server, serverIndex) => {
    const components = componentsOf(server);
    const unitPrice = server.unitPriceOverride != null
      ? server.unitPriceOverride
      : components.reduce((sum, component) => sum + (sellUnitKrw(component, ctx) ?? 0) * (component.quantity ?? 0), 0);
    const serverQty = Math.max(1, server.serverQuantity ?? 1);
    const chassis = components.find((component) => (component.category ?? '').toLowerCase() === 'chassis');
    const name = (chassis?.description ?? server.description ?? '').trim() || chassis?.optionPart || '서버';
    const specLine = chassis?.optionPart ? `CTO ${chassis.optionPart}` : (server.description ?? '').trim();
    const seenCategories = new Set<string>();
    const chips: Array<{ label: string; value: string; detail?: string }> = [];
    for (const component of components) {
      const category = (component.category ?? '').toUpperCase();
      if (!category || category === 'CHASSIS' || seenCategories.has(category)) continue;
      seenCategories.add(category);
      chips.push({ label: CHIP_LABEL[category] ?? category, value: shortSpec(component.description) || component.optionPart || '' });
      if (chips.length >= 6) break;
    }
    const condition = conditionText(server.condition);
    const briefText = buildServerDescription(
      components.flatMap((component) => {
        const category = toSpecCategory(component.category);
        return category ? [{ category, quantity: component.quantity ?? 1, descriptionRaw: component.description }] : [];
      }),
    );
    const briefLines = briefText ? briefText.split('\n') : [];
    return { index: serverIndex + 1, name, specLine, serverId: server.id, serverQty, unitPrice, condition, chips, items: components, briefLines,
      rentalTermMonths: null, rentalContractCostKrw: null };   // 판매 구성 — 임대 필드는 toRentalServerConfigs 가 덮는다
  });
}

/**
 * 서버 임대 구성들 — 판매 구성 뼈대에 임대값을 덮는다(모양 바꾸기).
 *   임대는 1대 월 임대단가가 서버 헤더행 unit_price_krw 에 박제돼 있다(부품 합산 아님).
 *   기간·임대총원가도 서버 헤더행에서 온다. toServerConfigs 는 서버를 등장 순서로 매핑하므로 같은 순서로 짝짓는다.
 */
export function toRentalServerConfigs(items: QuoteItem[], ctx: QuoteCtx): ServerConfig[] {
  const servers = items.filter((item) => item.lineType === 'server');
  return toServerConfigs(items, ctx).map((config, configIndex) => {
    const server = servers[configIndex];
    return { ...config,
      unitPrice: server.unitPriceKrw ?? 0,               // 1대 월 임대단가(박제)
      rentalTermMonths: server.rentalTermMonths,
      rentalContractCostKrw: server.rentalContractCostKrw };
  });
}
